/**
 * Rules for loading Slack channels page by page. Each page is one Slack call,
 * and Slack rate-limits `conversations.list` per workspace, so the browser
 * waits out a 429 instead of the API holding the request open.
 */

/** Slack answers rate limits with a Retry-After; past this, give up on the page. */
export const MAX_RATE_LIMIT_RETRIES = 5;

interface ChannelPage {
  nextCursor: string | null;
}

const httpStatus = (error: unknown) =>
  typeof error === 'object' && error !== null && 'status' in error
    ? error.status
    : undefined;

export const isRateLimited = (error: unknown) => httpStatus(error) === 429;

/** Milliseconds to wait before retrying a rate-limited page; 1s when Slack gave no delay. */
export const rateLimitDelayMs = (error: unknown) => {
  const seconds =
    typeof error === 'object' && error !== null && 'retryAfterSeconds' in error
      ? Number(error.retryAfterSeconds)
      : 0;
  return (seconds > 0 ? seconds : 1) * 1000;
};

/**
 * The workspace's channels fit in one page, so refetching them is a single
 * cheap call. Nothing loaded yet also counts: retrying the first page is cheap.
 */
export const fitsInOnePage = (pages: ChannelPage[] | undefined) =>
  !pages || (pages.length === 1 && pages[0]?.nextCursor === null);

/** Orders names that start with the query before names that only contain it. */
export const rankByPrefix = <T extends { name: string }>(
  channels: T[],
  query: string,
) => {
  const lowered = query.trim().toLowerCase();
  if (!lowered) return channels;
  const matches = channels.filter((channel) =>
    channel.name.toLowerCase().includes(lowered),
  );
  return [
    ...matches.filter((channel) =>
      channel.name.toLowerCase().startsWith(lowered),
    ),
    ...matches.filter(
      (channel) => !channel.name.toLowerCase().startsWith(lowered),
    ),
  ];
};
